import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, CircularProgress, MenuItem, TextField, Typography,
} from '@mui/material';
import { fetchRegisterMeta, submitNewExamineeRegistration } from '../studentPortalApi';
import { fieldSxFromTheme, getPortalTheme } from '../portalTheme';

const IS_OPEN_GEMARA = process.env.REACT_APP_EXAM_MODE === 'open-gemara';

const EMPTY = {
  phone: '',
  firstName: '',
  lastName: '',
  fatherName: '',
  fatherInLawName: '',
  city: '',
  address: '',
};

const kolelLabel = (opt) => {
  if (!opt) return '';
  return `${opt.name}${opt.city ? ` (${opt.city})` : ''}${opt.code != null ? ` — ${opt.code}` : ''}`;
};

const kolelFilterOpts = (opts, state) => {
  const q = state.inputValue.trim().toLowerCase();
  if (!q) return opts;
  return opts.filter(
    (o) =>
      o.name?.toLowerCase().includes(q) ||
      o.city?.toLowerCase().includes(q) ||
      String(o.code ?? '').includes(q)
  );
};

const classifySchedule = (schedule) => {
  const s = (schedule || '').trim();
  if (s.includes('בוקר') || s.includes('לפנה') || s.includes('לפני')) return 'morning';
  if (s.includes('אחה') || s.includes('צהר')) return 'afternoon';
  if (s.includes('ערב')) return 'evening';
  return 'unknown';
};

const RegisterStep = ({ onBack, theme }) => {
  const t = theme || getPortalTheme(process.env.REACT_APP_EXAM_MODE !== 'open-gemara');
  const fieldSx = fieldSxFromTheme(t);
  const autocompleteSx = {
    ...fieldSx,
    '& .MuiAutocomplete-popupIndicator': { color: t.accentSoft },
    '& .MuiAutocomplete-clearIndicator': { color: 'rgba(245,240,230,0.6)' },
  };
  const autocompleteListSx = {
    bgcolor: t.listBg,
    color: '#f5f0e6',
    '& .MuiAutocomplete-option': {
      '&[aria-selected="true"]': { bgcolor: t.accentMuted.replace('0.35', '0.2').replace('0.38', '0.2') },
      '&.Mui-focused': { bgcolor: t.accentMuted.replace('0.35', '0.12').replace('0.38', '0.12') },
    },
  };

  const [form, setForm] = useState(EMPTY);
  const [kolelMorning, setKolelMorning] = useState(null);
  const [kolelAfternoon, setKolelAfternoon] = useState(null);
  const [kolelEvening, setKolelEvening] = useState(null);
  const [cities, setCities] = useState([]);
  const [cityMode, setCityMode] = useState('list');
  const [kolels, setKolels] = useState([]);
  const [registrationOpen, setRegistrationOpen] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [booting, setBooting] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const meta = await fetchRegisterMeta();
        if (cancelled) return;
        setCities(meta.residenceCities || []);
        setCityMode(meta.residenceCityMode === 'free' ? 'free' : 'list');
        setKolels(meta.kolels || []);
        setRegistrationOpen(!!meta.registrationOpen);
        if (!meta.registrationOpen) {
          setError('הרישום למערכת סגור כרגע. פנו למשרד');
        }
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || 'שגיאה בטעינת הטופס');
      } finally {
        if (!cancelled) setBooting(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const kolelsForSlot = useMemo(() => {
    const bySlot = (slot) =>
      (kolels || []).filter((k) => {
        const c = classifySchedule(k.schedule);
        return c === slot || c === 'unknown' || (k.schedule || '').includes('כל היום');
      });
    return {
      morning: bySlot('morning'),
      afternoon: bySlot('afternoon'),
      evening: bySlot('evening'),
    };
  }, [kolels]);

  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (IS_OPEN_GEMARA) {
      if (!kolelMorning?._id) {
        setError('יש לבחור כולל');
        return;
      }
    } else if (!kolelMorning?._id && !kolelAfternoon?._id && !kolelEvening?._id) {
      setError('יש לבחור לפחות כולל אחד (לפנה״צ / אחה״צ / ערב)');
      return;
    }
    setLoading(true);
    try {
      const data = await submitNewExamineeRegistration({
        ...form,
        phone: form.phone.trim(),
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        fatherName: form.fatherName.trim(),
        fatherInLawName: form.fatherInLawName.trim(),
        address: form.address.trim(),
        kolelMorningId: kolelMorning?._id || null,
        kolelAfternoonId: IS_OPEN_GEMARA ? null : (kolelAfternoon?._id || null),
        kolelEveningId: IS_OPEN_GEMARA ? null : (kolelEvening?._id || null),
      });
      setSuccess(data.message || 'הרישום אושר בהצלחה. תתקבל אצלכם שיחה קולית עם הקוד האישי');
      setForm(EMPTY);
      setKolelMorning(null);
      setKolelAfternoon(null);
      setKolelEvening(null);
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בשליחת הבקשה');
    } finally {
      setLoading(false);
    }
  };

  const renderKolelSelect = (label, value, onChange, options, requiredHint = false) => (
    <Autocomplete
      options={options}
      getOptionLabel={kolelLabel}
      value={value}
      onChange={(_, val) => onChange(val)}
      filterOptions={kolelFilterOpts}
      isOptionEqualToValue={(opt, val) => opt._id === val?._id}
      clearOnEscape
      noOptionsText="לא נמצא כולל"
      sx={{ mt: 2, mb: 0.5, ...autocompleteSx }}
      ListboxProps={{ sx: autocompleteListSx }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder="חיפוש לפי שם, עיר או קוד..."
          helperText={requiredHint ? (IS_OPEN_GEMARA ? 'יש לבחור כולל' : 'יש לבחור לפחות כולל אחד') : undefined}
          sx={fieldSx}
        />
      )}
    />
  );

  if (booting) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress sx={{ color: t.progressColor }} />
      </Box>
    );
  }

  if (success) {
    return (
      <Box sx={{ maxWidth: 480, mx: 'auto', textAlign: 'center' }}>
        <Typography
          variant="h5"
          sx={{
            color: '#f5f0e6',
            fontFamily: '"Frank Ruhl Libre", "David Libre", serif',
            fontWeight: 700,
            mb: 2,
          }}
        >
          הרישום אושר
        </Typography>
        <Typography sx={{ color: 'rgba(245,240,230,0.8)', mb: 3, lineHeight: 1.8 }}>
          {success}
        </Typography>
        <Button
          onClick={onBack}
          sx={{
            minHeight: 48,
            px: 4,
            fontWeight: 700,
            borderRadius: 2,
            color: t.ctaColor,
            background: t.ctaBg,
          }}
        >
          חזרה לכניסה
        </Button>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 480, mx: 'auto' }}>
      <Typography
        variant="h5"
        sx={{
          color: '#f5f0e6',
          fontFamily: '"Frank Ruhl Libre", "David Libre", serif',
          fontWeight: 700,
          mb: 1,
          textAlign: 'center',
        }}
      >
        רישום לנבחן חדש
      </Typography>
      <Typography sx={{ color: 'rgba(245,240,230,0.75)', mb: 2.5, textAlign: 'center', fontSize: '0.95rem', lineHeight: 1.7 }}>
        מלאו את הפרטים. מיד לאחר הרישום תישלח אליכם שיחה קולית עם הקוד האישי.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

      <TextField fullWidth required label="מספר טלפון" value={form.phone} onChange={setField('phone')} margin="normal" inputProps={{ inputMode: 'tel', dir: 'ltr' }} sx={fieldSx} />
      <TextField fullWidth required label="שם פרטי" value={form.firstName} onChange={setField('firstName')} margin="normal" sx={fieldSx} />
      <TextField fullWidth required label="שם משפחה" value={form.lastName} onChange={setField('lastName')} margin="normal" sx={fieldSx} />
      <TextField fullWidth required label="שם האב" value={form.fatherName} onChange={setField('fatherName')} margin="normal" sx={fieldSx} />
      <TextField fullWidth label="שם חותן (לא חובה)" value={form.fatherInLawName} onChange={setField('fatherInLawName')} margin="normal" sx={fieldSx} />
      {cityMode === 'free' ? (
        <TextField
          fullWidth
          required
          label="עיר מגורים"
          value={form.city}
          onChange={setField('city')}
          margin="normal"
          sx={fieldSx}
        />
      ) : (
        <TextField
          select
          fullWidth
          required
          label="עיר מגורים"
          value={form.city}
          onChange={setField('city')}
          margin="normal"
          sx={fieldSx}
        >
          <MenuItem value=""><em>בחרו עיר...</em></MenuItem>
          {cities.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
        </TextField>
      )}
      <TextField fullWidth required label="כתובת מלאה" value={form.address} onChange={setField('address')} margin="normal" multiline minRows={2} sx={fieldSx} />

      {IS_OPEN_GEMARA ? (
        renderKolelSelect('כולל', kolelMorning, setKolelMorning, kolels, true)
      ) : (
        <>
          {renderKolelSelect('כולל לפנה"צ', kolelMorning, setKolelMorning, kolelsForSlot.morning, true)}
          {renderKolelSelect('כולל אחה"צ', kolelAfternoon, setKolelAfternoon, kolelsForSlot.afternoon)}
          {renderKolelSelect('כולל ערב', kolelEvening, setKolelEvening, kolelsForSlot.evening)}
        </>
      )}

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: 1.5, mt: 3 }}>
        <Button
          type="button"
          onClick={onBack}
          sx={{
            flex: 1,
            minHeight: 48,
            borderRadius: 2,
            color: 'rgba(245,240,230,0.85)',
            border: `1px solid ${t.accentBorder}`,
          }}
        >
          חזרה
        </Button>
        <Button
          type="submit"
          disabled={loading || !registrationOpen}
          sx={{
            flex: 2,
            minHeight: 48,
            fontWeight: 700,
            borderRadius: 2,
            color: t.ctaColor,
            background: t.ctaBg,
            '&:hover': { filter: 'brightness(1.06)' },
            '&.Mui-disabled': { background: t.ctaDisabledBg, color: t.ctaDisabledColor },
          }}
        >
          {loading ? <CircularProgress size={22} sx={{ color: t.ctaColor }} /> : 'שליחת בקשה'}
        </Button>
      </Box>
    </Box>
  );
};

export default RegisterStep;
